'use client';

import { createContext, useContext, ReactNode, useMemo, memo, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuthInitialization, useAuthError, useAuth } from '@/app/hooks/useAuth';
import { PermissionLoader } from './PermissionLoader';
import { TenantLoader } from '../../navigation/components/TenantLoader';
import type { AppDispatch, RootState } from '@/app/store';
import { resetForAccountChange, resetForTenantChange } from '@/app/store/slices/chatSlice';
import {
  resetForAccountChange as resetChatSessionForAccountChange,
  resetForTenantChange as resetChatSessionForTenantChange,
} from '@/app/store/slices/chatSessionSlice';
import { loadSessions } from '@/app/store/thunks/chatSessionThunks';

interface AuthContextType {
  isAuthenticated: boolean;
  isInitializing: boolean;
  isInitialized: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Provider that initializes authentication once at the app level
 * and provides auth state to all child components
 * UPDATED: Now uses the new auth system
 * OPTIMIZED: React.memo + useMemo for computed values
 */
export const AuthProvider = memo<AuthProviderProps>(function AuthProvider({ children }) {
  const { isInitialized, isInitializing } = useAuthInitialization();
  const { isAuthenticated } = useAuth(); // Get authentication state from main hook
  const { error } = useAuthError();

  // Watch the active account id and reset chat state whenever it changes.
  // This is the bulletproof reset path — fires regardless of which thunk /
  // sync action / storage update caused the change, because Redux only updates
  // `state.auth.activeAccountId` when the new value differs from the old.
  // Without this, the chat panel keeps the previous user's messages and reuses
  // the same `correlationId` after add-account / switch-account flows that
  // bypass the explicit `switchAccountThunk`.
  const dispatch = useDispatch<AppDispatch>();
  const activeAccountId = useSelector((s: RootState) => s.auth.activeAccountId);
  const previousAccountIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const previous = previousAccountIdRef.current;
    // Skip the very first render — the initial assignment is not a "change".
    if (previous === undefined) {
      previousAccountIdRef.current = activeAccountId ?? null;
      return;
    }
    if (previous === activeAccountId) return;
    previousAccountIdRef.current = activeAccountId ?? null;
    dispatch(resetForAccountChange());
    // Also reset the Spec B session drawer state. The chatSessionSlice already
    // listens for the multi-account thunks via a matcher, but the AuthProvider
    // path is the bulletproof catch-all that fires regardless of which code
    // path caused the change.
    dispatch(resetChatSessionForAccountChange());
  }, [activeAccountId, dispatch]);

  // Same pattern for the active tenant id within an account. When the user
  // switches tenants, their cached chat session list is scoped to the
  // previous tenant and the in-memory conversation may belong to a session
  // owned by the previous tenant (which would fail the server-side ownership
  // check on the next message). Reset both slices and refetch the drawer.
  const activeTenantId = useSelector(
    (s: RootState) => s.tenant.activeTenant?.tenantId ?? null
  );
  const previousTenantIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const previous = previousTenantIdRef.current;
    if (previous === undefined) {
      previousTenantIdRef.current = activeTenantId ?? null;
      return;
    }
    if (previous === activeTenantId) return;
    previousTenantIdRef.current = activeTenantId ?? null;
    dispatch(resetForTenantChange());
    dispatch(resetChatSessionForTenantChange());
    // Eagerly refetch the drawer list scoped to the new tenant so the next
    // open shows fresh data immediately. Skip on logout — activeTenantId
    // transitioning to null during sign-out would otherwise fire an
    // unauthenticated GET that returns 401 and noisy console errors.
    if (isAuthenticated && activeTenantId) {
      dispatch(loadSessions(undefined));
    }
  }, [activeTenantId, dispatch, isAuthenticated]);

  // Debug logging removed for production

  const authState: AuthContextType = useMemo(() => ({
    isAuthenticated,
    isInitializing: isInitializing && !isInitialized,
    isInitialized,
    error: error?.userMessage || null
  }), [isAuthenticated, isInitializing, isInitialized, error?.userMessage]);

  return (
    <AuthContext.Provider value={authState}>
      <PermissionLoader />
      <TenantLoader />
      {children}
    </AuthContext.Provider>
  );
});

/**
 * Hook to access the auth context
 * This should be used instead of useAuthInitialization in components
 * to avoid duplicate initialization
 */
export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

export default AuthProvider;
